// Git Connections Configuration Component
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  GitBranch,
  Plus,
  Trash2,
  RefreshCw,
  Check,
  X,
  Loader2,
  ExternalLink,
  Eye,
  EyeOff,
  Settings,
  Link2,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

// Git Provider Icons
const GitProviderIcon = ({ provider, className = "h-5 w-5" }) => {
  switch (provider) {
    case 'github':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      );
    case 'gitlab':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51L23 13.45a.84.84 0 0 1-.35.94z"/>
        </svg>
      );
    case 'bitbucket':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M.778 1.213a.768.768 0 0 0-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 0 0 .77-.646l3.27-20.03a.768.768 0 0 0-.768-.891zM14.52 15.53H9.522L8.17 8.466h7.561z"/>
        </svg>
      );
    case 'custom':
      return <Globe className={className} />;
    default:
      return <GitBranch className={className} />;
  }
};

const providerLabels = {
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
  custom: 'Auto-hébergé'
};

const providerColors = {
  github: 'bg-gray-900 text-white',
  gitlab: 'bg-orange-600 text-white',
  bitbucket: 'bg-blue-600 text-white',
  custom: 'bg-purple-600 text-white'
};

export default function GitConnectionsConfig({ userId }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [showToken, setShowToken] = useState(false);
  
  const [formData, setFormData] = useState({
    provider: 'github',
    name: '',
    token: '',
    base_url: ''
  });
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (userId) {
      loadConnections();
    }
  }, [userId]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/git-connections/?user_id=${userId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setConnections(data);
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
      toast.error('Erreur lors du chargement des connexions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.token) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/git-connections/?user_id=${userId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );
      
      if (response.ok) {
        toast.success('Connexion créée avec succès');
        setDialogOpen(false);
        setFormData({ provider: 'github', name: '', token: '', base_url: '' });
        loadConnections();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Erreur lors de la création de la connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!connectionToDelete) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/git-connections/${connectionToDelete}?user_id=${userId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Connexion supprimée');
        loadConnections();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    }
  };

  const handleTest = async (connectionId) => {
    setTesting(connectionId);
    try {
      const response = await fetch(
        `${API_URL}/api/git-connections/${connectionId}/test?user_id=${userId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Connexion valide (${data.username})`);
        loadConnections();
      } else {
        toast.error(`Échec de connexion: ${data.error || 'Token invalide'}`);
      }
    } catch (error) {
      console.error('Test error:', error);
      toast.error('Erreur lors du test de connexion');
    } finally {
      setTesting(null);
    }
  };

  const getProviderHelp = (provider) => {
    switch (provider) {
      case 'github':
        return {
          url: 'https://github.com/settings/tokens',
          text: 'Créez un Personal Access Token avec les scopes "repo" et "read:user"'
        };
      case 'gitlab':
        return {
          url: 'https://gitlab.com/-/user_settings/personal_access_tokens',
          text: 'Créez un Personal Access Token avec les scopes "api" et "read_user"'
        };
      case 'bitbucket':
        return {
          url: 'https://bitbucket.org/account/settings/app-passwords/',
          text: 'Créez un App Password avec les permissions "Repositories: Read"'
        };
      case 'custom':
        return {
          url: null,
          text: 'Entrez l\'URL de base de votre instance Git et le token d\'accès'
        };
      default:
        return { url: null, text: '' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Connexions Git</h3>
          <p className="text-sm text-muted-foreground">
            Connectez vos comptes GitHub, GitLab ou Bitbucket pour déployer facilement vos projets
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une connexion
        </Button>
      </div>

      {/* Connections List */}
      {connections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h4 className="text-lg font-medium mb-2">Aucune connexion Git</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Ajoutez une connexion pour synchroniser automatiquement vos dépôts
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une connexion
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {connections.map((conn) => (
            <Card key={conn.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-lg flex items-center justify-center",
                      providerColors[conn.provider]
                    )}>
                      <GitProviderIcon provider={conn.provider} className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{conn.name}</h4>
                        <Badge variant={conn.is_connected ? "default" : "destructive"}>
                          {conn.is_connected ? (
                            <><Check className="h-3 w-3 mr-1" /> Connecté</>
                          ) : (
                            <><X className="h-3 w-3 mr-1" /> Déconnecté</>
                          )}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        <span>{providerLabels[conn.provider]}</span>
                        {conn.username && (
                          <span className="flex items-center gap-1">
                            {conn.avatar_url && (
                              <img src={conn.avatar_url} alt="" className="w-4 h-4 rounded-full" />
                            )}
                            @{conn.username}
                          </span>
                        )}
                        {conn.base_url && (
                          <span className="flex items-center gap-1">
                            <Link2 className="h-3 w-3" />
                            {conn.base_url}
                          </span>
                        )}
                      </div>
                      {conn.last_synced && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Dernière sync: {new Date(conn.last_synced).toLocaleString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(conn.id)}
                      disabled={testing === conn.id}
                    >
                      {testing === conn.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      <span className="ml-2 hidden sm:inline">Tester</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setConnectionToDelete(conn.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Connection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Nouvelle connexion Git</DialogTitle>
            <DialogDescription>
              Ajoutez une connexion à votre compte Git pour synchroniser vos dépôts
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Provider Selection */}
            <div className="space-y-2">
              <Label>Fournisseur *</Label>
              <Select
                value={formData.provider}
                onValueChange={(value) => setFormData({ ...formData, provider: value, base_url: '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="github">
                    <div className="flex items-center gap-2">
                      <GitProviderIcon provider="github" className="h-4 w-4" />
                      GitHub
                    </div>
                  </SelectItem>
                  <SelectItem value="gitlab">
                    <div className="flex items-center gap-2">
                      <GitProviderIcon provider="gitlab" className="h-4 w-4" />
                      GitLab
                    </div>
                  </SelectItem>
                  <SelectItem value="bitbucket">
                    <div className="flex items-center gap-2">
                      <GitProviderIcon provider="bitbucket" className="h-4 w-4" />
                      Bitbucket
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Auto-hébergé
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="conn-name">Nom de la connexion *</Label>
              <Input
                id="conn-name"
                placeholder="Ex: Mon compte GitHub"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Base URL (for self-hosted) */}
            {(formData.provider === 'custom' || formData.provider === 'gitlab') && (
              <div className="space-y-2">
                <Label htmlFor="base-url">
                  URL de base {formData.provider === 'custom' ? '*' : '(optionnel)'}
                </Label>
                <Input
                  id="base-url"
                  placeholder={formData.provider === 'gitlab' ? 'https://gitlab.example.com' : 'https://git.example.com'}
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.provider === 'gitlab' 
                    ? 'Laissez vide pour GitLab.com'
                    : 'URL de votre instance Git auto-hébergée'}
                </p>
              </div>
            )}

            {/* Token */}
            <div className="space-y-2">
              <Label htmlFor="token">Token d'accès personnel *</Label>
              <div className="relative">
                <Input
                  id="token"
                  type={showToken ? "text" : "password"}
                  placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                  value={formData.token}
                  onChange={(e) => setFormData({ ...formData, token: e.target.value })}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Help text */}
              <div className="text-xs text-muted-foreground">
                {getProviderHelp(formData.provider).text}
                {getProviderHelp(formData.provider).url && (
                  <a
                    href={getProviderHelp(formData.provider).url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline mt-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Créer un token
                  </a>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connexion...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Connecter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette connexion ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les déploiements existants ne seront pas affectés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
